import { ReactNode } from 'react';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * A question, on its own card.
 *
 * The `id` is on the heading rather than on the card so that following a link to it puts
 * the question at the top of the viewport rather than the card's padding.
 *
 * These ids are load-bearing and are the legacy page's, unchanged. `#price-shield` is
 * where MagSpot's account rail sends a customer to read about auto-renewal
 * (`auto_renewals_about` in config/services/sites.php) and where two of the dated terms
 * documents link; `#first-issue` is the footer's and the institutional rail's "Delivery
 * Policy"; `#refund` is linked from the FAQ's own list. Renaming one breaks a link
 * somewhere else in the site.
 */
function Question({ id, question, children }: { id: string; question: string; children: ReactNode }) {
  return (
    <ContentCard>
      <h3 id={id}>{question}</h3>
      {children}
    </ContentCard>
  );
}

/**
 * /faq.
 *
 * A card per question, which is the institutional pages' section pattern — TopMags' FAQ
 * and DiscountMags' accessibility and copyright pages are the same shape. The words are
 * the legacy page's, unchanged.
 *
 * The legacy page grouped its questions under four headings and opened with a list of
 * links into them. Both are kept: the groups are what make eleven questions readable, and
 * the list is the page's own navigation.
 *
 * Heading levels are the one thing that changed. The questions were bold anchors inside a
 * card header — headings under nothing — and are `<h3>`s under their group's `<h2>` now.
 * The list's own group labels are not headings; see the note beside them.
 */
export default function Faq() {
  const { buildUrl } = useUrlGenerator();

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Frequently Asked Questions' }]} />

      <PageHeading eyebrow="Customer Information">Frequently Asked Questions</PageHeading>

      <div className="prose mt-6 flex flex-col gap-6">
        {/*
         * The group labels in this list are not headings, though the legacy page's were.
         * The same four words label the real sections further down, so making both
         * headings puts "Orders" into the document outline twice and a screen-reader user
         * navigating by heading lands on each of them once on the way past. These label a
         * list of links; the sections below are the page's structure.
         */}
        <ContentCard>
          <p className="font-semibold">Orders</p>
          <ul>
            <li>
              <a href="#first-issue">When will my first issue arrive?</a>
            </li>
            <li>
              <a href="#renew">How do I renew my subscription?</a>
            </li>
            <li>
              <a href="#double">Why am I receiving double issues?</a>
            </li>
            <li>
              <a href="#price-match">Do you price match?</a>
            </li>
            <li>
              <a href="#price-shield">What is PriceShield?</a>
            </li>
          </ul>

          <p className="font-semibold">Return Policy</p>
          <ul>
            <li>
              <a href="#refund">What is your policy on refunds and order cancellations?</a>
            </li>
          </ul>

          <p className="font-semibold">Delivery</p>
          <ul>
            <li>
              <a href="#foreign">Do you deliver outside of the U.S.?</a>
            </li>
            <li>
              <a href="#apo">Do you ship to APO addresses?</a>
            </li>
            <li>
              <a href="#correctional">Do you ship to correctional facilities?</a>
            </li>
            <li>
              <a href="#address">How can I change / update my address?</a>
            </li>
          </ul>

          <p className="font-semibold">Gifting</p>
          <ul>
            <li>
              <a href="#gift">Can I send a subscription as a gift?</a>
            </li>
          </ul>

          <p>
            If your question has not been answered please{' '}
            <a href={buildUrl('/contact')}>reach out to our customer service team</a>. You should
            receive a prompt response. Please kindly check your spam box folder if you don&apos;t
            receive a response within 48 hours or send us another message.
          </p>
        </ContentCard>

        <h2 className="mb-0 mt-2">Orders</h2>

        <Question id="first-issue" question="When will my first issue arrive?">
          <p>
            The industry standard wait time is 8-12 weeks to receive your first issue. Also, keep in
            mind that some magazine publishers pre-print mailing labels in advance which can delay
            the arrival of your first issue. If for some reason you have been waiting over 12 weeks
            for your first issue, <a href={buildUrl('/contact')}>let us know</a> and we would be
            happy to look into your order right away!
          </p>
        </Question>

        <Question id="renew" question="How do I renew my subscription?">
          <p>
            After you add a magazine to your cart, change the dropdown selection from
            &quot;New&quot; to &quot;Renewal&quot;. Once the publisher receives the order, they will
            tack on the additional year(s) to your current account with them. Ensure that the
            delivery name and address matches the current subscription exactly to avoid a duplicate
            account being created.
          </p>
        </Question>

        <Question id="double" question="Why am I receiving double issues?">
          <p>
            If you&apos;re receiving two issues of the same magazine, the problem could either be
            you ordered the same magazine twice, or you subscribed as a new subscriber while you
            already have another subscription. But don&apos;t worry please{' '}
            <a href={buildUrl('/contact')}>contact us</a> and we can resolve the issue.
          </p>
        </Question>

        <Question id="price-match" question="Do you price match?">
          <p>
            Yes, we will price match any authorized competitor pricing within 30 days of your order
            date. Please <a href={buildUrl('/contact')}>provide us</a> with the URL of where we can
            find the offer you&apos;re requesting to match.
          </p>
        </Question>

        <Question id="price-shield" question="What is PriceShield?">
          <p>
            PriceShield is our automatic renewal program that ensures you never miss an issue. Your
            subscription will automatically renew with a minimum discount of 10% off the list price
            at the time of each renewal. If for any reason we cannot offer this discount, we will
            notify you and will not automatically renew your subscription.
          </p>
          <p>
            If your magazine is discontinued or otherwise becomes unavailable, we&apos;ll
            automatically provide you with a subscription to a similar magazine of equal or greater
            value, at the same or better price (after the 10% minimum discount). We&apos;ll send you
            advance notice of the substitution, and if you don&apos;t want the substitute magazine,
            you can cancel before the renewal is processed.
          </p>
          <p>
            We&apos;ll send you an initial reminder 30 days prior to your subscription renewal.
            Then, 7 days before the renewal, we&apos;ll provide a follow-up email detailing the
            exact amount you will be charged.
          </p>
          {/*
           * The legacy answer's second link was `{customer.discountlocklink}`, which is a
           * fixed `customer.php?xCmd=dlshow` rather than anything per-customer. That is the
           * pre-V2 DiscountLock page; #1813 moved these two brands' onto the V2 account
           * route, and `auto_renewals_list` in config/services/sites.php names this one for
           * MagSpot. Same page, and it carries the visitor back here after signing in
           * rather than leaving them on the login screen.
           */}
          <p>
            PriceShield comes with a satisfaction guarantee, so if at any time during your
            subscription you are unhappy with the service, you may cancel it at any time by{' '}
            <a href={buildUrl('/contact')}>contacting us</a> or{' '}
            <a href={buildUrl('/my-account/price-shield')}>logging into your account</a>.
          </p>
        </Question>

        <h2 className="mb-0 mt-2">Return Policy</h2>

        <Question id="refund" question="What is your policy on refunds and order cancellations?">
          <p>
            You can view our Return Policy by{' '}
            <a href={buildUrl('/refund-policy')}>clicking here</a>.
          </p>
        </Question>

        <h2 className="mb-0 mt-2">Delivery</h2>

        <Question id="foreign" question="Do you deliver outside of the U.S.?">
          <p>We only deliver to the United States at this time.</p>
        </Question>

        <Question id="apo" question="Do you ship to APO addresses?">
          <p>Yes, we do!</p>
        </Question>

        <Question id="correctional" question="Do you ship to correctional facilities?">
          <p>
            Yes, we do, however, each correctional facility sets its own rules for inmate magazines
            on what they can and cannot receive. Please be sure to check if the magazine you are
            ordering will be allowed by the recipient&apos;s facility. Inmate ID numbers are
            required by most facilities right after the Last Name. Please look up facility
            requirements before ordering, prisons will not deliver mail if the names and numbers do
            not match.
          </p>
        </Question>

        <Question id="address" question="How can I change / update my address?">
          <p>
            If you ordered with an incorrect address, or you recently moved and need to update your
            address, you can <a href={buildUrl('/contact')}>contact us</a> and we&apos;ll happily
            update your order with the new address.
          </p>
        </Question>

        <h2 className="mb-0 mt-2">Gifting</h2>

        <Question id="gift" question="Can I send a subscription as a gift?">
          <p>
            Yes! Buy them a gift subscription for their birthday, anniversary, housewarming or for
            the holidays! When checking out you can add individual addresses for each subscription.
          </p>
        </Question>
      </div>
    </>
  );
}

Faq.layoutName = 'Layouts.Institutional';
